/**
 * Validação e conversão de imagem no navegador. Puro: sem Vue, sem Firebase.
 *
 * O re-encode por canvas é o que dá compressão previsível, mas é também o que
 * valida de verdade: se decodificou, é imagem, e o que sai do canvas não carrega
 * mais nada do arquivo de origem — EXIF, GPS e qualquer payload embutido ficam
 * para trás. Os magic bytes servem só para recusar cedo, com mensagem amigável.
 */

/** Teto do que vai para o Storage. Depois do resize é folga: raramente é atingido. */
export const MAX_STORED_BYTES = 2 * 1024 * 1024
/** Guarda pré-decode. Sem ela uma "bomba de descompressão" mata a aba. */
export const MAX_SOURCE_BYTES = 15 * 1024 * 1024
export const MAX_SIDE = 1600
/** O canvas guarda RGBA: 40 MP já são 160 MB de backing store. */
const MAX_PIXELS = 40e6
const WEBP_QUALITY = 0.82

export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
/** Para o atributo accept do input. Lembrando que arrastar e soltar o ignora. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(',')

export type ImageErrorCode = 'image-type' | 'image-too-large' | 'image-decode'

export type PrepareResult =
    | { ok: true; blob: Blob }
    | { ok: false; code: ImageErrorCode }

export function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) {
        const mb = bytes / (1024 * 1024)
        return `${mb % 1 === 0 ? mb : mb.toFixed(1)} MB`
    }
    return `${Math.round(bytes / 1024)} kB`
}

/**
 * Tipo real pelos 12 primeiros bytes. O file.type vem do sistema operacional e é
 * trivialmente falsificável — renomear um .txt para .png engana o accept, não isto.
 */
async function sniffType(file: Blob): Promise<string | null> {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer())
    const is = (offset: number, ...bytes: number[]) =>
        bytes.every((byte, i) => head[offset + i] === byte)

    if (is(0, 0xff, 0xd8, 0xff)) return 'image/jpeg'
    if (is(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png'
    if (is(0, 0x47, 0x49, 0x46, 0x38)) return 'image/gif'
    //RIFF....WEBP
    if (is(0, 0x52, 0x49, 0x46, 0x46) && is(8, 0x57, 0x45, 0x42, 0x50)) return 'image/webp'
    return null
}

type DecodedImage = {
    source: CanvasImageSource
    width: number
    height: number
    release: () => void
}

/**
 * imageOrientation: 'from-image' evita escrever um parser de EXIF só para não
 * deitar foto de celular tirada na vertical.
 * Onde createImageBitmap não existe ou recusa o arquivo, o <img> resolve:
 * o padrão de image-orientation do CSS já é from-image em todos os motores atuais.
 */
async function decodeImage(file: Blob): Promise<DecodedImage> {
    if (typeof createImageBitmap === 'function') {
        try {
            //o lib.dom do TS 4.1 só conhece 'none' | 'flipY', daí a dupla conversão
            const options = {imageOrientation: 'from-image'} as unknown as ImageBitmapOptions
            const bitmap = await createImageBitmap(file, options)
            return {
                source: bitmap,
                width: bitmap.width,
                height: bitmap.height,
                release: () => bitmap.close()
            }
        } catch (err) {
            //cai no <img>
        }
    }

    const url = URL.createObjectURL(file)
    const image = new Image()
    image.src = url
    try {
        await image.decode()
    } catch (err) {
        URL.revokeObjectURL(url)
        throw err
    }
    return {
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        release: () => URL.revokeObjectURL(url)
    }
}

function encode(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
    return new Promise(resolve => canvas.toBlob(resolve, type, quality))
}

/**
 * Valida, redimensiona e converte. Devolve o Blob que deve ser armazenado.
 *
 * GIF passa sem conversão: o canvas achataria no primeiro quadro, e matar a
 * animação em silêncio é o tipo de coisa que volta como bug.
 */
export async function prepareImage(file: File): Promise<PrepareResult> {
    if (file.size > MAX_SOURCE_BYTES) return {ok: false, code: 'image-too-large'}

    const type = await sniffType(file)
    if (!type || ACCEPTED_TYPES.indexOf(type) < 0) return {ok: false, code: 'image-type'}

    if (type === 'image/gif') {
        if (file.size > MAX_STORED_BYTES) return {ok: false, code: 'image-too-large'}
        return {ok: true, blob: file.slice(0, file.size, type)}
    }

    let decoded: DecodedImage
    try {
        decoded = await decodeImage(file)
    } catch (err) {
        return {ok: false, code: 'image-decode'}
    }

    const canvas = document.createElement('canvas')
    try {
        const {width, height} = decoded
        if (!width || !height) return {ok: false, code: 'image-decode'}
        if (width * height > MAX_PIXELS) return {ok: false, code: 'image-too-large'}

        //O min(1, ...) impede upscale: ampliar uma imagem pequena só engorda o arquivo
        const scale = Math.min(1, MAX_SIDE / Math.max(width, height))
        canvas.width = Math.max(1, Math.round(width * scale))
        canvas.height = Math.max(1, Math.round(height * scale))

        const context = canvas.getContext('2d')
        if (!context) return {ok: false, code: 'image-decode'}
        context.imageSmoothingQuality = 'high'
        context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height)

        //toBlob pode chamar de volta com null E pode devolver PNG em silêncio quando
        //não sabe codificar o tipo pedido — conferir o type é obrigatório
        let blob = await encode(canvas, 'image/webp', WEBP_QUALITY)
        if (!blob || blob.type !== 'image/webp') {
            //JPEG não tem alpha: sem o fundo branco todo PNG transparente vira preto
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, canvas.width, canvas.height)
            context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height)
            blob = await encode(canvas, 'image/jpeg', WEBP_QUALITY)
        }

        if (!blob) return {ok: false, code: 'image-decode'}
        if (blob.size > MAX_STORED_BYTES) return {ok: false, code: 'image-too-large'}
        return {ok: true, blob}
    } finally {
        decoded.release()
        //devolve o backing store sem esperar o coletor
        canvas.width = 0
        canvas.height = 0
    }
}

export default {prepareImage, formatBytes, ACCEPT_ATTRIBUTE, MAX_STORED_BYTES, MAX_SIDE}
